import * as THREE from 'three';
import type { MaterialProvider, MaterialRequest } from '../../human/types';
import { makeSkinDetailNormal } from './detailMaps';

/**
 * Fournisseur de matériaux "rendu beauté" pour le module human :
 * peau à diffusion sous-surfacique approchée, yeux humides, cheveux anisotropes, tissus avec sheen.
 */
export interface MaterialOptions {
  renderer: THREE.WebGLRenderer;
  msaa: boolean;
  /** Taille max des textures (px) : 1024 sur mobile → 4× moins de mémoire GPU. */
  maxTextureSize?: number;
}

let maxTexSize = 8192;

const loader = new THREE.TextureLoader();
const texCache = new Map<string, Promise<THREE.Texture>>();

function loadTex(url: string, srgb: boolean, anisotropy: number): THREE.Texture {
  // Retourne une texture immédiatement utilisable ; l'image arrive ensuite.
  const tex = new THREE.Texture();
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.anisotropy = anisotropy;
  tex.flipY = true;
  let p = texCache.get(url);
  if (!p) {
    p = loader.loadAsync(url);
    texCache.set(url, p);
  }
  p.then((src) => { tex.image = fitImage(src.image as HTMLImageElement, maxTexSize); tex.needsUpdate = true; }).catch((e) => console.warn('texture', url, e));
  return tex;
}

/** Réduit une image trop grande pour le palier de qualité (une seule fois par image, mise en cache). */
const fitted = new WeakMap<object, CanvasImageSource>();
function fitImage(img: HTMLImageElement, max: number): CanvasImageSource {
  const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
  if (!w || Math.max(w, h) <= max) return img;
  const done = fitted.get(img);
  if (done) return done;
  const k = max / Math.max(w, h);
  const c = document.createElement('canvas');
  c.width = Math.round(w * k); c.height = Math.round(h * k);
  const g = c.getContext('2d')!;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, 0, 0, c.width, c.height);
  fitted.set(img, c);
  return c;
}

// ---- Patch de shading peau : wrap lighting par canal + transmission arrière douce ----
const SKIN_DIFFUSE_ORIG = 'reflectedLight.directDiffuse += irradiance * BRDF_Lambert( material.diffuseContribution ) * ( 1.0 - F );';
/** Tissu : léger « wrap » diffus (la lumière traverse et diffuse dans les fibres) — adoucit les ombres franches sous
 *  les volumes (dessous de poitrine, plis) qui faisaient des taches presque noires sur les tissus clairs. */
const CLOTH_DIFFUSE_NEW = /* glsl */`
  {
    float ndlRaw = dot( geometryNormal, directLight.direction );
    float wrapped = clamp( ( ndlRaw + 0.32 ) / 1.32, 0.0, 1.0 );
    reflectedLight.directDiffuse += wrapped * directLight.color * BRDF_Lambert( material.diffuseContribution ) * ( 1.0 - F );
  }`;
const SKIN_DIFFUSE_NEW = /* glsl */`
  {
    float ndlRaw = dot( geometryNormal, directLight.direction );
    vec3 w = uSkinWrap;
    vec3 wrapped = clamp( ( vec3( ndlRaw ) + w ) / ( ( 1.0 + w ) * ( 1.0 + w ) ), 0.0, 1.0 );
    // Teinte de diffusion au terminateur : la lumière "rougit" en pénétrant la peau.
    float term = clamp( 1.0 - abs( ndlRaw ) * 2.2, 0.0, 1.0 ) * step( -w.r, ndlRaw );
    vec3 skinIrr = ( wrapped + uSkinScatter * term * 0.08 ) * directLight.color;
    reflectedLight.directDiffuse += skinIrr * BRDF_Lambert( material.diffuseContribution ) * ( 1.0 - F );
    // Transmission (oreilles, nez, contre-jours) : approximation à la Barré-Brisebois.
    vec3 tl = normalize( directLight.direction + geometryNormal * 0.4 );
    float back = pow( saturate( dot( geometryViewDir, -tl ) ), 5.0 ) * uSkinBacklight;
    reflectedLight.directDiffuse += back * directLight.color * uSkinScatter * material.diffuseContribution;
  }`;

// ---- Yeux : dilatation de la pupille (centres/rayons mesurés sur les textures MakeHuman) ----
const EYE_MAP_FRAGMENT = /* glsl */`
#ifdef USE_MAP
  vec2 uvE = vMapUv;
  vec2 ec = uvE.x > 0.5 ? vec2( 0.7075, 0.7035 ) : vec2( 0.2945, 0.2980 );
  vec2 ed = uvE - ec;
  float er = length( ed );
  const float irisR = 0.098;
  const float p0 = 0.034;
  float p1 = clamp( p0 * uPupil, 0.018, 0.07 );
  if ( er < irisR ) {
    float rt = er < p1 ? er * ( p0 / p1 ) : p0 + ( er - p1 ) * ( irisR - p0 ) / ( irisR - p1 );
    uvE = ec + ed * ( rt / max( er, 1e-5 ) );
  }
  vec4 sampledDiffuseColor = texture2D( map, uvE );
  diffuseColor *= sampledDiffuseColor;
#endif
`;

// ---- Cheveux : Kajiya-Kay (deux lobes décalés le long de la normale) ----
const HAIR_DECL = /* glsl */`
uniform float uHairShift1; uniform float uHairShift2; uniform float uHairExp1; uniform float uHairExp2;
uniform float uHairSpec1; uniform float uHairSpec2;
vec3 hairT = vec3( 0.0, 1.0, 0.0 );
`;
const HAIR_TANGENT = /* glsl */`
  {
    // Direction des mèches = dP/dv (les cartes MakeHuman sont orientées le long de V).
    vec3 dpx = dFdx( - vViewPosition ), dpy = dFdy( - vViewPosition );
    #ifdef USE_MAP
      vec2 dux = dFdx( vMapUv ), duy = dFdy( vMapUv );
    #else
      vec2 dux = vec2( 1.0, 0.0 ), duy = vec2( 0.0, 1.0 );
    #endif
    float det = dux.x * duy.y - dux.y * duy.x;
    vec3 t = ( dpy * dux.x - dpx * duy.x ) * ( det < 0.0 ? -1.0 : 1.0 );
    t -= normal * dot( normal, t );
    float l = length( t );
    hairT = l > 1e-8 ? t / l : normalize( cross( normal, vec3( 1.0, 0.0, 0.0 ) ) + 1e-4 );
    // Orientation cohérente racine → pointe (les UV des cartes peuvent être inversées d'une carte à l'autre).
    vec3 upV = normalize( ( viewMatrix * vec4( 0.0, 1.0, 0.0, 0.0 ) ).xyz );
    if ( dot( hairT, upV ) > 0.0 ) hairT = - hairT;
  }
`;
const HAIR_SPEC = /* glsl */`
  #ifdef USE_MAP
  {
    vec3 H = normalize( directLight.direction + geometryViewDir );
    vec3 t1 = normalize( hairT + geometryNormal * uHairShift1 );
    vec3 t2 = normalize( hairT + geometryNormal * uHairShift2 );
    float th1 = dot( t1, H ), th2 = dot( t2, H );
    float s1 = pow( sqrt( max( 0.0, 1.0 - th1 * th1 ) ), uHairExp1 );
    float s2 = pow( sqrt( max( 0.0, 1.0 - th2 * th2 ) ), uHairExp2 );
    float vis = smoothstep( 0.0, 0.5, dot( geometryNormal, directLight.direction ) );
    reflectedLight.directSpecular += directLight.color * vis * gHairEdge * ( s1 * uHairSpec1 + s2 * uHairSpec2 * material.diffuseColor );
  }
  #endif
`;
const hairUniforms: Record<string, THREE.IUniform> = {
  uHairShift1: { value: -0.12 }, uHairShift2: { value: 0.18 },
  uHairExp1: { value: 320 }, uHairExp2: { value: 90 },
  uHairSpec1: { value: 0.035 }, uHairSpec2: { value: 0.06 },
};

/**
 * Mouvement des cheveux (voir presence/HairSway) : décalage des pointes dans l'espace de liaison, pondéré par la
 * distance sous l'ancrage (le haut du crâne reste fixe, les pointes suivent le ressort).
 */
export interface HairSwayUniforms {
  uHairSway: THREE.IUniform<THREE.Vector3>;
  uHairAnchor: THREE.IUniform<number>;
  uHairLen: THREE.IUniform<number>;
}

/**
 * Tissus mous : jusqu'à 4 pressions (doigt, caresse) creusent la peau ET le tissu porté, dans l'espace de
 * liaison (commun au corps et aux vêtements). La normale est inclinée pour que le creux se voie à l'éclairage.
 * uPress[i] = (centre xyz, rayon) ; uPressDepth[i] = profondeur (m, négatif = léger rebond).
 */
export interface PressUniforms {
  uPress: THREE.IUniform<THREE.Vector4[]>;
  uPressDepth: THREE.IUniform<number[]>;
  /** Assise (repère personnage) : x, z du centre, hauteur du coussin, intensité (0 = debout). */
  uSeat: THREE.IUniform<THREE.Vector4>;
  /** Rayon de l'assise sous elle (m). */
  uSeatR: THREE.IUniform<number>;
}

const PRESS_DECL = `uniform vec4 uPress[4];
uniform float uPressDepth[4];
uniform vec4 uSeat;
uniform float uSeatR;
`;
/** Assise : ce qui passerait sous le coussin s'aplatit dessus (6 mm d'enfoncement au plus), avec un léger
 *  renflement vers l'extérieur — la chair et le tissu s'écrasent au lieu de traverser le siège. Après le skinning. */
const SEAT_APPLY = `#include <skinning_vertex>
  if ( uSeat.w > 0.001 ) {
    vec2 sd = transformed.xz - uSeat.xy;
    float sr = length( sd );
    float sw = ( 1.0 - smoothstep( uSeatR - 0.06, uSeatR + 0.02, sr ) ) * uSeat.w;
    float sdep = transformed.y - uSeat.z;
    // Au-delà de ~4 cm sous le coussin, ce n'est pas un appui (jambe qui pend devant le siège) : rien.
    sw *= 1.0 - smoothstep( 0.03, 0.045, -sdep );
    if ( sw > 0.0 && sdep < 0.0 ) {
      float sflat = -0.006 * ( 1.0 - exp( sdep / 0.006 ) );
      float lift = ( sflat - sdep ) * sw;
      transformed.y += lift;
      transformed.xz += ( sd / max( sr, 1e-3 ) ) * lift * 0.3;
    }
  }`;
const PRESS_NORMAL = `
  vec3 pressDisp = vec3( 0.0 );
  for ( int i = 0; i < 4; i ++ ) {
    float dep = uPressDepth[ i ];
    if ( abs( dep ) < 1e-5 ) continue;
    vec4 P = uPress[ i ];
    vec3 dv = position - P.xyz;
    float r2 = P.w * P.w;
    float d2 = dot( dv, dv ) / r2;
    if ( d2 > 9.0 ) continue;
    float w = exp( - d2 );
    pressDisp -= objectNormal * ( dep * w );
    vec3 tang = dv - objectNormal * dot( dv, objectNormal );
    objectNormal = normalize( objectNormal - tang * ( 2.0 * dep * w / r2 ) );
  }
#include <skinbase_vertex>`;
const PRESS_APPLY = `#include <begin_vertex>
  transformed += pressDisp;`;

/** Ajoute les pressions (tissus mous) à un vertex shader de matériau standard/physique skinné. */
function injectPress(vs: string, seat = false): string {
  const out = PRESS_DECL + vs.replace('#include <skinbase_vertex>', PRESS_NORMAL).replace('#include <begin_vertex>', PRESS_APPLY);
  // Aplatissement sur l'assise : peau seulement (le tissu sous elle est caché dans le coussin ; celui qui pend devant
  // doit rester libre).
  return seat ? out.replace('#include <skinning_vertex>', SEAT_APPLY) : out;
}

/**
 * Maquillage (peau) : rouge à lèvres (limité au vermillon par la rougeur de la texture), fard à paupières, blush,
 * vernis à ongles. Masques par sommet (attribut `makeup` : lèvres, paupières, ongles ; `blush` pour les joues),
 * calculés depuis les poids des os du visage et des doigts (voir app/Makeup).
 */
export interface MakeupUniforms {
  uLipColor: THREE.IUniform<THREE.Color>;
  /** x = intensité, y = brillance. */
  uLip: THREE.IUniform<THREE.Vector2>;
  /** Seuils de rougeur relative (r - v) / r qui distinguent le vermillon de la peau. */
  uLipEdge: THREE.IUniform<THREE.Vector2>;
  uShadowColor: THREE.IUniform<THREE.Color>;
  uShadowAmt: THREE.IUniform<number>;
  uMkBlushColor: THREE.IUniform<THREE.Color>;
  uMkBlushAmt: THREE.IUniform<number>;
  uNailColor: THREE.IUniform<THREE.Color>;
  /** x = intensité, y = brillance. */
  uNail: THREE.IUniform<THREE.Vector2>;
}

const MAKEUP_VERT_DECL = `attribute vec3 makeup;
varying vec3 vMakeup;
`;
const MAKEUP_FRAG_DECL = `uniform vec3 uLipColor;
uniform vec2 uLip;
uniform vec2 uLipEdge;
uniform vec3 uShadowColor;
uniform float uShadowAmt;
uniform vec3 uMkBlushColor;
uniform float uMkBlushAmt;
uniform vec3 uNailColor;
uniform vec2 uNail;
varying vec3 vMakeup;
`;
const MAKEUP_COLOR = `#include <color_fragment>
  float mkGloss = 0.0;
  {
    vec3 base = diffuseColor.rgb;
    float lum = dot( base, vec3( 0.299, 0.587, 0.114 ) );
    // Rouge à lèvres : seulement sur le vermillon (texels rougeâtres sous le masque des lèvres).
    float red = ( base.r - base.g ) / max( base.r, 1e-3 );
    float lipM = clamp( vMakeup.x, 0.0, 1.0 ) * smoothstep( uLipEdge.x, uLipEdge.y, red ) * uLip.x;
    diffuseColor.rgb = mix( diffuseColor.rgb, uLipColor * ( 0.5 + 1.7 * lum ), lipM );
    // Fard à paupières (teinte multiplicative, garde le grain de la peau).
    float shM = clamp( vMakeup.y, 0.0, 1.0 ) * uShadowAmt;
    diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * uShadowColor * 1.7, shM * 0.8 );
    // Blush de maquillage (même masque que le rougissement).
    float blM = clamp( vBlush, 0.0, 1.0 ) * uMkBlushAmt;
    diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * uMkBlushColor * 1.6, blM * 0.7 );
    // Vernis.
    float nM = clamp( vMakeup.z, 0.0, 1.0 ) * uNail.x;
    diffuseColor.rgb = mix( diffuseColor.rgb, uNailColor * ( 0.75 + 0.5 * lum ), nM );
    mkGloss = max( lipM * uLip.y, nM * uNail.y );
  }`;
const MAKEUP_ROUGH = `#include <metalnessmap_fragment>
  roughnessFactor = mix( roughnessFactor, 0.13, clamp( mkGloss, 0.0, 1.0 ) );`;

function injectMakeup(sh: { vertexShader: string; fragmentShader: string }) {
  sh.vertexShader = MAKEUP_VERT_DECL + sh.vertexShader.replace('#include <begin_vertex>', `#include <begin_vertex>
  vMakeup = makeup;`);
  sh.fragmentShader = MAKEUP_FRAG_DECL + sh.fragmentShader
    .replace('#include <color_fragment>', MAKEUP_COLOR)
    .replace('#include <metalnessmap_fragment>', MAKEUP_ROUGH);
}

/**
 * Teinte des vêtements : chaque matériau de vêtement a ses propres uniforms (couleur, force). La teinte garde le motif
 * et le grain (luminance du tissu), seule la couleur change. Registre global : id d'asset → teinte voulue.
 */
export interface ClothTint { color: string; amount: number }
export const clothTints = new Map<string, ClothTint>();

/** Appelle `fn` dès que l'image de la texture est disponible. */
function whenLoaded(tex: THREE.Texture, fn: () => void) {
  if (tex.image && (tex.image as { width?: number }).width) { fn(); return; }
  let tries = 0;
  const id = setInterval(() => {
    if ((tex.image && (tex.image as { width?: number }).width) || ++tries > 120) { clearInterval(id); if (tex.image) fn(); }
  }, 250);
}

/** Luminance moyenne (linéaire) d'une texture, échantillonnée en 32×32. */
function averageLuminance(tex: THREE.Texture, outColor?: THREE.Color): number {
  try {
    const c = document.createElement('canvas');
    c.width = c.height = 32;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(tex.image as CanvasImageSource, 0, 0, 32, 32);
    const d = g.getImageData(0, 0, 32, 32).data;
    const lin = (v: number) => { v /= 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    let sum = 0, n = 0, r = 0, g2 = 0, b = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 128) continue; // zones transparentes (découpes) ignorées
      const lr = lin(d[i]), lg = lin(d[i + 1]), lb = lin(d[i + 2]);
      sum += 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
      r += lr; g2 += lg; b += lb;
      n++;
    }
    if (outColor && n) outColor.setRGB(r / n, g2 / n, b / n);
    return n ? sum / n : 0.2;
  } catch { return 0.2; }
}
const CLOTH_TINT_FRAG = `#include <color_fragment>
  {
    float l = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
    // Luminance relative à la moyenne du tissu : le motif et le grain restent, la couleur prend la teinte voulue.
    float rel = clamp( l / max( uClothAvgLum, 0.015 ), 0.0, 2.2 );
    vec3 tinted = uClothTint * mix( 1.0, rel, 0.75 );
    diffuseColor.rgb = mix( diffuseColor.rgb, tinted, uClothTintAmt );
  }`;

/**
 * Vent dans les jupes : l'ourlet frémit (attribut `flutter` : 0 à la taille → 1 à l'ourlet), dans l'espace de liaison.
 * uWind = direction × amplitude (m) ; uWindTime = temps (s). Discret : quelques centimètres au plus, à l'horizontale.
 */
export interface WindUniforms {
  uWind: THREE.IUniform<THREE.Vector3>;
  uWindTime: THREE.IUniform<number>;
  /** Marche (repère personnage) : x = balancement latéral (retard sur les hanches), y = évasement, z = vers l'avant. */
  uWalk: THREE.IUniform<THREE.Vector3>;
}
export const windUniforms: WindUniforms = { uWind: { value: new THREE.Vector3() }, uWindTime: { value: 0 }, uWalk: { value: new THREE.Vector3() } };
/**
 * Collision tissu / peau au rendu (voir HumanImpl.updateAnchors) : le point de peau d'ancrage est déformé avec le
 * squelette comme la peau elle-même ; un tissu passé sous le plan tangent de cette peau est repoussé juste au-dessus
 * (écart anchorPos.w). Sans ancrage (normale nulle, attribut absent) : rien.
 */
/** Capsules des bras (repère personnage) : xyz = extrémités, w de uArmA = rayon (0 = inactive). Voir ArmCollision. */
export const ARM_CAPSULES = 8;
export const armUniforms = {
  uArmA: { value: Array.from({ length: ARM_CAPSULES }, () => new THREE.Vector4()) },
  uArmB: { value: Array.from({ length: ARM_CAPSULES }, () => new THREE.Vector4()) },
};
const ANCHOR_DECL = `uniform vec4 uArmA[ ${ARM_CAPSULES} ];
uniform vec4 uArmB[ ${ARM_CAPSULES} ];
attribute vec4 anchorPos;
attribute vec3 anchorNrm;
attribute vec4 anchorIdx;
attribute vec4 anchorWgt;
`;
const ANCHOR_APPLY = `#include <skinning_vertex>
#ifdef USE_SKINNING
  // Point de peau d'ancrage, déformé comme la peau (s'il existe).
  bool hasAnchor = dot( anchorNrm, anchorNrm ) > 0.25;
  vec3 ap = vec3( 0.0 ), an = vec3( 0.0 );
  if ( hasAnchor ) {
    mat4 am = anchorWgt.x * getBoneMatrix( anchorIdx.x ) + anchorWgt.y * getBoneMatrix( anchorIdx.y )
      + anchorWgt.z * getBoneMatrix( anchorIdx.z ) + anchorWgt.w * getBoneMatrix( anchorIdx.w );
    am = bindMatrixInverse * am * bindMatrix;
    ap = ( am * vec4( anchorPos.xyz, 1.0 ) ).xyz;
    an = normalize( ( am * vec4( anchorNrm, 0.0 ) ).xyz );
  }
  // Le tissu LÂCHE (volant, jupe : à plus de ~1,5 cm de la peau) s'écarte des bras, toujours du même côté : sous la
  // main si elle est posée dessus (plus loin de la peau que lui), par-dessus si elle est dessous — jamais fendu en deux
  // autour d'elle. Le tissu ajusté suit le corps : c'est la collision bras / corps (ArmCollision) qui le protège.
  float loose = hasAnchor ? smoothstep( 0.008, 0.022, dot( transformed - ap, an ) ) : 1.0;
  for ( int i = 0; i < ${ARM_CAPSULES}; i ++ ) {
    if ( loose <= 0.0 ) break;
    float cr = uArmA[ i ].w;
    if ( cr <= 0.0 ) continue;
    vec3 ca = uArmA[ i ].xyz, cab = uArmB[ i ].xyz - ca;
    float cab2 = max( dot( cab, cab ), 1e-8 );
    float ct = clamp( dot( transformed - ca, cab ) / cab2, 0.0, 1.0 );
    vec3 cc = ca + cab * ct;
    vec3 cd = transformed - cc;
    float cl = length( cd );
    if ( cl >= cr ) continue;
    if ( !hasAnchor ) { if ( cl > 1e-5 ) transformed += cd * ( ( cr - cl ) / cl ); continue; }
    cr = cl + ( cr - cl ) * loose;
     vec3 cu = dot( cc - ap, an ) > dot( transformed - ap, an ) ? -an : an;
    // Sortie le long de u : |w + s·u⊥| = r — composantes perpendiculaires à l'axe sur le fût, vecteurs entiers aux
    // extrémités (sphères), sinon les bouts de capsule projetteraient le tissu au loin.
    vec3 cdx = cab * inversesqrt( cab2 );
    bool ccap = ct <= 0.0 || ct >= 1.0;
    vec3 cup = ccap ? cu : cu - cdx * dot( cu, cdx );
    float cuu = dot( cup, cup );
    if ( cuu < 0.04 ) { if ( cl > 1e-5 ) transformed += cd * ( ( cr - cl ) / cl ); continue; }
    vec3 cw = ccap ? cd : cd - cdx * dot( cd, cdx );
    float cbb = dot( cw, cup ), ccc = dot( cw, cw ) - cr * cr;
    float csd = clamp( ( - cbb + sqrt( max( cbb * cbb - cuu * ccc, 0.0 ) ) ) / cuu, 0.0, 2.0 * cr );
    // Vers la peau : jamais sous l'écart voulu (un bras appuyé contre le flanc recouvre un peu le tissu, sans le déchirer).
    if ( dot( cu, an ) < 0.0 ) csd = min( csd, max( dot( transformed - ap, an ) - anchorPos.w, 0.0 ) );
    transformed += cu * csd;
  }
  // La peau garde le dernier mot : un tissu passé sous le plan tangent de son point d'ancrage est repoussé au-dessus.
  if ( hasAnchor ) {
    float asd = dot( transformed - ap, an );
    // Au-delà de 8 cm derrière le plan, ce n'est plus cette peau-là (autre côté du membre) : rien.
    if ( asd < anchorPos.w && asd > -0.08 ) transformed += an * ( anchorPos.w - asd );
  }
#endif`;
const WIND_DECL = `attribute float flutter;
uniform vec3 uWind;
uniform float uWindTime;
uniform vec3 uWalk;
`;
const WIND_APPLY = `#include <begin_vertex>
  if ( flutter > 0.0 ) {
    float ph = dot( position, vec3( 9.0, 4.0, 7.0 ) );
    float gust = 0.55 + 0.45 * sin( uWindTime * 0.7 + 1.3 );
    float wave = sin( uWindTime * 3.1 + ph ) * 0.6 + sin( uWindTime * 5.3 + ph * 1.7 ) * 0.25;
    vec3 side = normalize( cross( uWind + vec3( 1e-5 ), vec3( 0.0, 1.0, 0.0 ) ) + vec3( 1e-5 ) );
    transformed += ( uWind * ( gust + 0.35 * wave ) + side * length( uWind ) * 0.4 * wave ) * ( flutter * flutter );
    // Marche : x = balancement latéral, y = évasement (la jupe s'ouvre autour des jambes qui avancent), z = avant.
    vec2 wr = transformed.xz;
    float wl = length( wr );
    transformed.xz += ( wl > 1e-4 ? wr / wl : vec2( 0.0 ) ) * uWalk.y * ( flutter * flutter );
    transformed.xz += uWalk.xz * ( flutter * flutter );
  }`;

/**
 * Couleur des cheveux : teinte qui garde mèches et reflets (luminance relative à la moyenne de la texture).
 * Partagée (une seule coiffure à la fois) ; les sourcils suivent à moitié.
 */
export interface HairTintUniforms { uHairTint: THREE.IUniform<THREE.Color>; uHairTintAmt: THREE.IUniform<number>; uHairAvgLum: THREE.IUniform<number>; uHairAvgColor: THREE.IUniform<THREE.Color> }
export const hairTint: HairTintUniforms = { uHairTint: { value: new THREE.Color(1, 1, 1) }, uHairTintAmt: { value: 0 }, uHairAvgLum: { value: 0.2 }, uHairAvgColor: { value: new THREE.Color(0.2, 0.12, 0.08) } };
const HAIR_TINT_DECL = `float gHairEdge = 1.0; // opacité du texel (bords de mèches) : atténue le reflet
uniform vec3 uHairTint;
uniform float uHairTintAmt;
uniform float uHairAvgLum;
uniform vec3 uHairAvgColor;
`;
const hairTintFrag = (scale: string, edgeToAverage: boolean) => `#include <color_fragment>
  // Bords des mèches (texels semi-transparents, souvent blancs dans la texture) : sans correction, un liseré clair
  // en dents de scie et des rectangles laiteux apparaissent. On les fond dans la couleur moyenne de la chevelure.
  ${edgeToAverage
    ? 'gHairEdge = smoothstep( 0.35, 0.95, diffuseColor.a ); diffuseColor.rgb = mix( uHairAvgColor * 0.8, diffuseColor.rgb, smoothstep( 0.3, 0.92, diffuseColor.a ) );'
    : 'diffuseColor.rgb *= mix( 0.35, 1.0, smoothstep( 0.2, 0.85, diffuseColor.a ) );'}
  {
    float l = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
    float rel = clamp( l / max( uHairAvgLum, 0.01 ), 0.0, 2.5 );
    // Mèches claires → teinte éclaircie ; racines sombres → teinte assombrie (garde la profondeur).
    vec3 tinted = uHairTint * mix( 1.0, rel, 0.85 );
    diffuseColor.rgb = mix( diffuseColor.rgb, tinted, uHairTintAmt * ${scale} );
  }`;

const HAIR_SWAY_VERTEX = `
  {
    float hw = clamp( ( uHairAnchor - position.y ) / uHairLen, 0.0, 1.0 );
    transformed += uHairSway * ( hw * hw * ( 3.0 - 2.0 * hw ) );
  }`;

export function createMaterialProvider(opts: MaterialOptions): { provider: MaterialProvider; skinUniforms: Record<string, THREE.IUniform>; eyeUniforms: Record<string, THREE.IUniform>; hairSway: HairSwayUniforms; press: PressUniforms; makeup: MakeupUniforms; hairTint: HairTintUniforms } {
  const makeup: MakeupUniforms = {
    uLipColor: { value: new THREE.Color(0.55, 0.08, 0.12) }, uLip: { value: new THREE.Vector2(0, 0.5) }, uLipEdge: { value: new THREE.Vector2(0.34, 0.46) },
    uShadowColor: { value: new THREE.Color(0.55, 0.45, 0.42) }, uShadowAmt: { value: 0 },
    uMkBlushColor: { value: new THREE.Color(0.95, 0.55, 0.55) }, uMkBlushAmt: { value: 0 },
    uNailColor: { value: new THREE.Color(0.6, 0.06, 0.1) }, uNail: { value: new THREE.Vector2(0, 0.8) },
  };
  const press: PressUniforms = {
    uPress: { value: [0, 1, 2, 3].map(() => new THREE.Vector4(0, 0, 0, 0.03)) },
    uPressDepth: { value: [0, 0, 0, 0] },
    uSeat: { value: new THREE.Vector4(0, 0, 0, 0) },
    uSeatR: { value: 0.25 },
  };
  const hairSway: HairSwayUniforms = {
    uHairSway: { value: new THREE.Vector3() },
    uHairAnchor: { value: 1.6 },
    uHairLen: { value: 0.3 },
  };
  const aniso = Math.min(8, opts.renderer.capabilities.getMaxAnisotropy());
  maxTexSize = opts.maxTextureSize ?? 8192;
  const detail = makeSkinDetailNormal();
  const skinUniforms: Record<string, THREE.IUniform> = {
    uSkinWrap: { value: new THREE.Vector3(0.4, 0.2, 0.14) },
    uSkinScatter: { value: new THREE.Color(0.9, 0.36, 0.28) },
    uSkinBacklight: { value: 0.16 },
    /** Rougissement (0..1), piloté par la présence ; localisé par l'attribut de sommet `blush`. */
    uBlush: { value: 0 },
  };

  const eyeUniforms: Record<string, THREE.IUniform> = {
    /** Échelle de la pupille (1 = texture d'origine ; 0,7 contractée ; 1,5 dilatée). */
    uPupil: { value: 1 },
  };

  const cutout = (m: THREE.Material, threshold = 0.02) => {
    if (opts.msaa) { m.alphaToCoverage = true; (m as THREE.MeshStandardMaterial).alphaTest = threshold; }
    // Sans MSAA, le tramage (alphaHash) scintille sans TAA : découpe nette, plus stable.
    else { (m as THREE.MeshStandardMaterial).alphaTest = Math.max(0.35, threshold); }
  };

  const provider: MaterialProvider = (req: MaterialRequest) => {
    const t = req.textures;
    const map = t.diffuse ? loadTex(t.diffuse, true, aniso) : null;
    const normalMap = t.normal ? loadTex(t.normal, false, aniso) : null;
    const color = new THREE.Color(...req.diffuseColor);
    // Les .mhmat MakeHuman prévoyaient un shader "litsphere" : on neutralise les teintes pensées pour lui.
    if (map && (req.kind === 'hair' || req.kind === 'eyebrows' || req.kind === 'eyelashes')) color.setRGB(1, 1, 1);
    if (!map && color.getHex() === 0) {
      if (req.kind === 'eyebrows') color.setRGB(0.04, 0.026, 0.02);
      else if (req.kind === 'eyelashes') color.setRGB(0.03, 0.022, 0.02);
      else if (req.kind === 'hair') color.setRGB(0.12, 0.07, 0.045);
    }

    switch (req.kind) {
      case 'skin': {
        const m = new THREE.MeshPhysicalMaterial({
          map, color,
          roughness: 0.56,
          specularIntensity: 0.22,
          ior: 1.4,
          sheen: 0.08, sheenRoughness: 0.65, sheenColor: new THREE.Color(0.5, 0.42, 0.4),
          envMapIntensity: 0.8,
          normalMap: normalMap ?? detail,
          normalScale: new THREE.Vector2(0.28, 0.28),
          roughnessMap: normalMap ? null : detail,
        });
        if (!normalMap) {
          // La carte de détail est tuilée : transformation propre à normalMap/roughnessMap.
          const d = detail.clone();
          d.repeat.set(42, 42);
          d.needsUpdate = true;
          m.normalMap = d;
          m.roughnessMap = d;
        }
        m.name = 'skin:' + req.assetId;
        m.onBeforeCompile = (sh) => {
          Object.assign(sh.uniforms, skinUniforms);
          sh.vertexShader = 'attribute float blush;\nvarying float vBlush;\n' +
            sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvBlush = blush;');
          Object.assign(sh.uniforms, press);
          sh.vertexShader = injectPress(sh.vertexShader, true);
          sh.fragmentShader = 'uniform vec3 uSkinWrap;\nuniform vec3 uSkinScatter;\nuniform float uSkinBacklight;\nuniform float uBlush;\nvarying float vBlush;\n' +
            sh.fragmentShader
              .replace('#include <map_fragment>', `#include <map_fragment>
                // Rougissement : teinte rosée multiplicative, localisée par l'attribut de sommet.
                diffuseColor.rgb = mix( diffuseColor.rgb, diffuseColor.rgb * vec3( 1.08, 0.74, 0.76 ), clamp( vBlush * uBlush, 0.0, 0.85 ) );`)
              .replace('#include <lights_physical_pars_fragment>',
                THREE.ShaderChunk.lights_physical_pars_fragment.replace(SKIN_DIFFUSE_ORIG, SKIN_DIFFUSE_NEW));
          Object.assign(sh.uniforms, makeup);
          injectMakeup(sh);
        };
        m.customProgramCacheKey = () => 'skin-sss-seat-v5';
        return m;
      }
      case 'eyes': {
        // Globe oculaire : la texture MakeHuman contient aussi la cornée (zone alpha 0) → découpée ici,
        // la cornée est rendue à part par `createCornea` (couche spéculaire additive).
        const m = new THREE.MeshPhysicalMaterial({ map, color, roughness: 0.38, specularIntensity: 0.4, name: 'eyes', alphaTest: 0.5 });
        // Pupille vivante : remappage radial de l'iris (les deux yeux partagent la même texture MakeHuman).
        m.onBeforeCompile = (sh) => {
          Object.assign(sh.uniforms, eyeUniforms);
          sh.fragmentShader = 'uniform float uPupil;\n' + sh.fragmentShader.replace('#include <map_fragment>', EYE_MAP_FRAGMENT);
        };
        m.customProgramCacheKey = () => 'eye-pupil-v1';
        return m;
      }
      case 'eyebrows':
      case 'eyelashes': {
        const m = new THREE.MeshStandardMaterial({ map, color, roughness: 0.92, metalness: 0, side: THREE.DoubleSide, name: req.kind,
          envMapIntensity: 0.25, opacity: req.kind === 'eyebrows' ? 0.8 : 0.9 });
        cutout(m);
        if (req.kind === 'eyebrows') {
          // Les sourcils suivent (à moitié) la couleur des cheveux.
          m.onBeforeCompile = (sh) => {
            Object.assign(sh.uniforms, hairTint);
            sh.fragmentShader = HAIR_TINT_DECL + sh.fragmentShader.replace('#include <color_fragment>', hairTintFrag('0.45', false));
          };
          m.customProgramCacheKey = () => 'eyebrows-tint-v3';
        }
        return m;
      }
      case 'hair': {
        // Cheveux : diffus doux + double reflet Kajiya-Kay le long des mèches (tangente déduite des UV).
        const m = new THREE.MeshStandardMaterial({ map, color, normalMap, roughness: 0.62, metalness: 0, side: THREE.DoubleSide, name: 'hair' });
        cutout(m, 0.22); // coupe les texels quasi transparents (franges colorées par les contre-jours)
        m.onBeforeCompile = (sh) => {
          Object.assign(sh.uniforms, hairUniforms, hairSway);
          sh.vertexShader = 'uniform vec3 uHairSway;\nuniform float uHairAnchor;\nuniform float uHairLen;\n' +
            sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>' + HAIR_SWAY_VERTEX);
          Object.assign(sh.uniforms, hairTint);
          sh.fragmentShader = HAIR_TINT_DECL + HAIR_DECL + sh.fragmentShader
            .replace('#include <color_fragment>', hairTintFrag('1.0', true))
            .replace('#include <normal_fragment_maps>', '#include <normal_fragment_maps>\n' + HAIR_TANGENT)
            .replace('#include <lights_physical_pars_fragment>',
              THREE.ShaderChunk.lights_physical_pars_fragment.replace(SKIN_DIFFUSE_ORIG, SKIN_DIFFUSE_ORIG + HAIR_SPEC));
        };
        m.customProgramCacheKey = () => 'hair-kk-tint-v7';
        if (map) whenLoaded(map, () => { hairTint.uHairAvgLum.value = averageLuminance(map, hairTint.uHairAvgColor.value); });
        return m;
      }
      case 'teeth':
        // Émail : légèrement plus clair et chaud que la texture MakeHuman, un peu de "lumière intérieure".
        {
          const m = new THREE.MeshPhysicalMaterial({ map, color, roughness: 0.28, specularIntensity: 0.5,
            emissive: new THREE.Color(0.05, 0.045, 0.04), emissiveMap: map, name: 'teeth' });
          // La texture MakeHuman donne un émail gris (sRGB ~160) : on éclaircit les zones peu saturées (dents)
          // vers un ivoire naturel, sans toucher à la gencive rose.
          m.onBeforeCompile = (sh) => {
            sh.fragmentShader = sh.fragmentShader.replace('#include <map_fragment>', `#include <map_fragment>
              {
                float mx = max( diffuseColor.r, max( diffuseColor.g, diffuseColor.b ) );
                float sat = ( mx - min( diffuseColor.r, min( diffuseColor.g, diffuseColor.b ) ) ) / max( mx, 1e-3 );
                float enamel = 1.0 - smoothstep( 0.12, 0.3, sat );
                diffuseColor.rgb = mix( diffuseColor.rgb, min( diffuseColor.rgb * 1.9, vec3( 0.82 ) ) * vec3( 1.0, 0.96, 0.88 ), enamel );
              }`);
          };
          m.customProgramCacheKey = () => 'teeth-v2';
          return m;
        }
      case 'tongue':
        return new THREE.MeshPhysicalMaterial({ map, color, roughness: 0.38, clearcoat: 0.6, clearcoatRoughness: 0.2, name: 'tongue' });
      case 'clothes':
      default: {
        const m = new THREE.MeshPhysicalMaterial({
          map, color, normalMap, roughness: 0.78, metalness: 0,
          sheen: 0.6, sheenRoughness: 0.6, sheenColor: new THREE.Color(0.5, 0.5, 0.5),
          side: req.backfaceCull ? THREE.FrontSide : THREE.DoubleSide, name: 'clothes:' + req.assetId,
        });
        if (req.transparent) cutout(m);
        // Le tissu se creuse avec la peau sous le doigt ; teinte personnalisable (uniforms propres au matériau).
        const tint = clothTints.get(req.assetId);
        const tintU = { uClothTint: { value: new THREE.Color(tint?.color ?? '#ffffff').convertSRGBToLinear() }, uClothTintAmt: { value: tint?.amount ?? 0 }, uClothAvgLum: { value: 0.2 } };
        if (map) whenLoaded(map, () => { tintU.uClothAvgLum.value = averageLuminance(map); });
        m.userData.tint = tintU;
        m.onBeforeCompile = (sh) => {
          Object.assign(sh.uniforms, press, tintU, windUniforms, armUniforms);
          sh.vertexShader = ANCHOR_DECL + WIND_DECL + injectPress(sh.vertexShader).replace('#include <begin_vertex>', WIND_APPLY)
            .replace('#include <skinning_vertex>', ANCHOR_APPLY);
          sh.fragmentShader = 'uniform vec3 uClothTint;\nuniform float uClothTintAmt;\nuniform float uClothAvgLum;\n' + sh.fragmentShader
            .replace('#include <color_fragment>', CLOTH_TINT_FRAG)
            .replace('#include <lights_physical_pars_fragment>', THREE.ShaderChunk.lights_physical_pars_fragment.replace(SKIN_DIFFUSE_ORIG, CLOTH_DIFFUSE_NEW));
        };
        m.customProgramCacheKey = () => 'clothes-press-tint-wind-walk-anchor-arms-v11';
        return m;
      }
    }
  };
  return { provider, skinUniforms, eyeUniforms, hairSway, press, makeup, hairTint };
}

/**
 * Cornée : même géométrie que l'œil, ne garde que la zone transparente de la texture et n'ajoute
 * que les reflets (mélange additif, couleur noire) → œil humide avec "catchlights" nets.
 */
export function createCornea(eyes: THREE.Mesh): THREE.Mesh {
  const src = eyes.material as THREE.MeshPhysicalMaterial;
  const m = new THREE.MeshPhysicalMaterial({
    color: 0x000000, map: src.map, roughness: 0.04, metalness: 0, specularIntensity: 1, ior: 1.376,
    clearcoat: 1, clearcoatRoughness: 0.02,
    transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, name: 'cornea',
  });
  m.onBeforeCompile = (sh) => {
    sh.fragmentShader = sh.fragmentShader.replace('#include <map_fragment>',
      '#ifdef USE_MAP\n if ( texture2D( map, vMapUv ).a > 0.5 ) discard;\n#endif');
  };
  m.customProgramCacheKey = () => 'cornea-v1';
  const isSkinned = (eyes as THREE.SkinnedMesh).isSkinnedMesh;
  let c: THREE.Mesh;
  if (isSkinned) {
    const sk = eyes as THREE.SkinnedMesh;
    const cs = new THREE.SkinnedMesh(sk.geometry, m);
    cs.bind(sk.skeleton, sk.bindMatrix);
    c = cs;
  } else c = new THREE.Mesh(eyes.geometry, m);
  c.morphTargetInfluences = eyes.morphTargetInfluences;
  c.morphTargetDictionary = eyes.morphTargetDictionary;
  c.renderOrder = (eyes.renderOrder ?? 0) + 1;
  c.name = 'cornea';
  return c;
}
